/**
 * @file: This file holds the controller of the Report page.
 *
 * The page is one window over the play log, for every scene at once. The
 * controller owns the window controls, the scene filter and the search box,
 * and it calls the two transformers. `ReportState` turns the results into the
 * model the template reads, so no rule of the report is in this file.
 *
 * The template reads one view model, which is built again after every command.
 * A binding that builds a new array on every digest would never settle.
 *
 * The window goes to the server as UTC, with the zone of the browser beside it.
 * The log holds UTC text and the operator reads festival time, so the server
 * needs the zone to cut a festival day at local midnight.
 */

import moment from 'moment'
import { windowProblem, WINDOW_PROBLEM_TEXT } from '../../../shared/play-report'
import { combineDateAndTime } from 'common/date-time'
import { iconForMediaType, labelForMediaType } from 'common/media-icon'
import { ReportState } from './managers/report-state'
import { csvText } from './managers/csv'
import './play-report.component.less'

/**
 * The widest preset covers thirty days: today and the twenty-nine days before
 * it. The server refuses a window over 31 days, and a day of a change to or
 * from summer time is 23 or 25 hours long. Twenty-nine days back keeps the
 * span inside the limit on every such day.
 */
const LAST_DAYS_BACK = 29

/** The byte order mark. Danish Excel opens the file without it as Latin-1. */
const BYTE_ORDER_MARK = '\uFEFF'

/**
 * Now, rounded up to the next minute. The controls hold a date and a clock
 * time without seconds. A window that ends at this minute leaves out the plays
 * of the minute the operator is in.
 */
const nextMinute = () => moment().add(1, 'minute').startOf('minute')

/** The window of each preset button, as a pair of moments. */
const PRESETS = Object.freeze({
  today: () => [moment().startOf('day'), nextMinute()],
  yesterday: () => [moment().subtract(1, 'day').startOf('day'), moment().startOf('day')],
  month: () => [moment().subtract(LAST_DAYS_BACK, 'days').startOf('day'), nextMinute()]
})

/**
 * What the page reads from a scene. A change in another field of a scene must
 * not rebuild the view model.
 */
const sceneSignature = scenes =>
  JSON.stringify(scenes.map(scene => [scene.id, scene.name, scene.color]))

class PlayReportController {
  constructor ($scope, $log, $window, $document, toaster, ApiService, SceneService) {
    'ngInject'

    this._$scope = $scope
    this._$log = $log
    this._$window = $window
    this._$document = $document
    this._toaster = toaster
    this._ApiService = ApiService
    this._SceneService = SceneService

    this.state = new ReportState()
    this.scenes = []
    // The scene of the filter. Null is every scene, and null is also what the
    // select needs to show its first option.
    this.sceneId = null
    this.search = ''
    this.loading = false
    // Which preset button is on. Any change of a date or a time turns it off.
    this.preset = 'today'
    this.window = { fromDate: null, fromTime: null, toDate: null, toTime: null }
    // The request of the last result. The hours of a media must ask about the
    // window that is on the page, and not about a control the operator has
    // changed since.
    this._request = null
    // The number of the newest request of each kind. A slow result of an older
    // request must not overwrite a newer one.
    this._newest = { report: 0, hours: 0 }
    this._sceneSignature = ''
    this._unsubscribe = null

    this._setPreset('today')
    this.refresh()
  }

  $onInit () {
    this._useScenes(this._SceneService.getAll() || [])
    this._unsubscribe = this._SceneService.subscribe(() => this._scenesChanged())
    this.load()
  }

  $onDestroy () {
    if (this._unsubscribe) {
      this._unsubscribe()
      this._unsubscribe = null
    }
  }

  /** The Font Awesome class of a media type, as the other lists draw it. */
  iconFor (type) {
    return iconForMediaType(type)
  }

  /** The word for a media type, as the report writes it in a sentence. */
  labelFor (type) {
    return labelForMediaType(type)
  }

  /** A preset button sets the window and reads it at once. */
  usePreset (name) {
    this._setPreset(name)
    return this.load()
  }

  /** A date or a time the operator wrote is no preset any more. */
  windowChanged () {
    this.preset = null
    return this.load()
  }

  /** The search box narrows the table. The window on the server is the same. */
  searchChanged () {
    this.state.setSearch(this.search)
    // The tiles, the findings and the open media are about the window. Only
    // the table changes with the search.
    this.view = { ...this.view, rows: this.state.rows() }
  }

  /**
   * Read the window from the server and build the page from the result.
   *
   * A preset is read again on every load. A page that stands open on Today
   * must show today up to this minute, and not the minute the page opened.
   *
   * A window that cannot be read asks nothing and says nothing. The operator
   * is between two keystrokes in a date field, and a toast on every one of
   * them is noise.
   */
  load () {
    if (this.preset) {
      this._setPreset(this.preset)
    }

    const request = this._readWindow()

    if (!request) {
      return
    }

    const generation = this._start('report')
    // A result of the hours belongs to the window before this one.
    this._start('hours')
    this.loading = true

    return this._ApiService.call('playreport/getPlayReport', request)
      .then(report => this._whenNewest('report', generation, () => {
        this.loading = false
        this._request = request
        this.state.setReport(report, request.timeZone)
        this.refresh()
      }))
      .catch(error => this._whenNewest('report', generation, () => {
        this.loading = false
        this._failed(error, 'Could not read the play log')
      }))
  }

  /**
   * Open one media in the right surface and ask for its hours.
   *
   * The row is on the page before the hours arrive, so the operator sees the
   * numbers of the media at once and the columns a moment later.
   */
  selectMedia (row) {
    // Without a result there is no window to ask about.
    if (!this._request) {
      return
    }

    const generation = this._start('hours')

    this.state.select(row)
    this.refresh()

    return this._ApiService
      .call('playreport/getPlayHours', { ...this._request, type: row.type, name: row.name })
      .then(response => this._whenNewest('hours', generation, () => {
        this.state.setHours(response && response.hours)
        this.refresh()
      }))
      .catch(error => this._whenNewest('hours', generation, () =>
        this._failed(error, 'Could not read the hours of the media')))
  }

  /** Close the open media, so Findings comes back. */
  closeMedia () {
    this._start('hours')
    this.state.close()
    this.refresh()
  }

  /**
   * Write the window to a file the sponsor can open.
   *
   * The export holds every media of the window, and not only the rows the
   * search leaves on the page. The file is the window, as the design says.
   */
  exportCsv () {
    const rows = this.state.csvRows()

    if (!rows.length) {
      this._toaster.error('There is nothing to write in this window', 'Nothing to export')
      return
    }

    const blob = new this._$window.Blob([BYTE_ORDER_MARK + csvText(rows)], { type: 'text/csv;charset=utf-8' })
    const url = this._$window.URL.createObjectURL(blob)
    const page = this._$document[0]
    const anchor = page.createElement('a')

    anchor.href = url
    anchor.download = this._fileName()
    // Firefox follows the anchor only while it is in the document.
    page.body.appendChild(anchor)
    anchor.click()
    page.body.removeChild(anchor)
    // The browser reads the blob after this function returns. A URL that is
    // revoked at once gives an empty file in Chrome.
    this._$window.setTimeout(() => this._$window.URL.revokeObjectURL(url), 0)
  }

  /**
   * Build the view model the template reads. Angular calls a binding on every
   * digest, so a query that returns new objects runs here and not there.
   */
  refresh () {
    this.view = {
      tiles: this.state.tiles(),
      sceneColumns: this.state.sceneColumns(),
      rows: this.state.rows(),
      findings: this.state.findings(),
      selected: this.state.selected()
    }
  }

  /**
   * Set the window from a preset button. Today runs to this minute, so the
   * tiles show the day as it is now and not the day up to midnight.
   */
  _setPreset (name) {
    const build = PRESETS[name]

    if (!build) {
      return
    }

    const [from, to] = build()
    this.preset = name
    this.window = {
      fromDate: from.toDate(),
      fromTime: from.toDate(),
      toDate: to.toDate(),
      toTime: to.toDate()
    }
  }

  /** Mark a new request of one kind, and give the number of it. */
  _start (kind) {
    this._newest = { ...this._newest, [kind]: this._newest[kind] + 1 }
    return this._newest[kind]
  }

  /** Do the work only while no newer request of that kind was made. */
  _whenNewest (kind, generation, act) {
    if (this._newest[kind] === generation) {
      act()
    }
  }

  /** The window of the controls as the transformers take it, or null. */
  _readWindow () {
    const from = combineDateAndTime(this.window.fromDate, this.window.fromTime)
    const to = combineDateAndTime(this.window.toDate, this.window.toTime)

    // The sentence stands in the toolbar until the window is readable again.
    this.windowProblem = this._problemText(from, to)

    if (this.windowProblem) {
      return null
    }

    return {
      from: from.toISOString(),
      to: to.toISOString(),
      sceneId: this.sceneId ? Number(this.sceneId) : null,
      // The zone of the browser, as an IANA name. The server cuts a festival
      // day at local midnight, and a window of a month can cross the change to
      // or from summer time. One offset would be wrong for half of those days.
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }

  /**
   * The sentence for a window the report cannot read, or null.
   *
   * The two rules of the window are in `shared/`, and they answer with a code.
   * The server and the page write their own sentence for the same code.
   */
  _problemText (from, to) {
    if (!from || !to) {
      return 'Set a From time and a To time'
    }
    return WINDOW_PROBLEM_TEXT[windowProblem(from.valueOf(), to.valueOf())] || null
  }

  /** The name of the export file, after the first day of the window. */
  _fileName () {
    const from = this._request ? moment(this._request.from) : moment()
    return `play-report-${from.format('YYYY-MM-DD')}.csv`
  }

  /**
   * The scenes arrive after login, so a reload straight onto Report can find
   * them empty and show "Scene 7" for a scene that has a name.
   *
   * The service reports every change of every scene. Only the id, the name and
   * the colour reach this page, so the rest do not rebuild the view model.
   */
  _scenesChanged () {
    this._$scope.$applyAsync(() => {
      const scenes = this._SceneService.getAll() || []

      if (sceneSignature(scenes) === this._sceneSignature) {
        return
      }

      this._useScenes(scenes)
      this.refresh()
    })
  }

  /** Put the scenes of the app on the page and in the model. */
  _useScenes (scenes) {
    this._sceneSignature = sceneSignature(scenes)
    this.scenes = scenes
    this.state.setScenes(scenes)
  }

  /** The API rejects with a string, and a thrown Error has a message. Both reach the toast. */
  _failed (error, title) {
    const message = (error && error.message) || String(error || 'Unknown error')
    this._$log.error(`[PlayReport] ${title}: ${message}`, error)
    this._toaster.error(message, title)
  }
}

export default {
  controller: PlayReportController,
  template: require('./play-report.tpl.html').default
}

export { PlayReportController }
